import { Injectable, inject, DestroyRef, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

/**
 * True while MSAL is dispatching or processing an interactive redirect.
 *
 * The interaction marker is written before MSAL navigates to Entra, while an
 * OAuth response can still be present after the browser returns. Checking both
 * closes the two windows in which a PWA update reload would cancel or corrupt
 * the redirect flow.
 */
export function isMsalInteractionInProgress(
  location: Pick<Location, 'hash' | 'search'>,
  storage: Storage,
): boolean {
  const authResponse = `${location.hash}&${location.search}`;
  const hasAuthResponse =
    /[#?&](code|error)=/.test(authResponse) && /[#?&]state=/.test(authResponse);
  if (hasAuthResponse) {
    return true;
  }

  try {
    for (let i = 0; i < storage.length; i++) {
      if (storage.key(i)?.endsWith('interaction.status')) {
        return true;
      }
    }
  } catch {
    // Browsers can deny storage access in restricted contexts. With no positive
    // interaction signal, preserve the existing update behavior.
  }

  return false;
}

/**
 * PwaUpdateService
 *
 * Drives PWA updates via the Angular service worker, with no user-facing UI.
 *
 * The service worker (enabled only in production builds — see app.config.ts)
 * downloads a new app version in the background whenever the deployed hashed
 * assets change. Activating that version requires a page reload, and a reload
 * throws away in-memory state (e.g. an unsent chat draft), so we pick the
 * moment to reload automatically instead of prompting:
 *
 * 1. Update detection runs for every client. Polling and re-checking on tab
 *    focus pre-download the next version into the SW cache, so a browser user's
 *    own next full reload lands on the new version immediately.
 *
 * 2. Installed / standalone PWAs auto-reload while backgrounded — the user
 *    never sees it and returns on the new version.
 *
 * 3. Browser tabs promote on their next in-app navigation. A tab is pinned to
 *    the version it booted on and reads translations once at bootstrap, so it
 *    keeps serving that version's cached `en.json` — the newest keys render raw
 *    until it reloads onto the promoted version. NavigationStart is the safe
 *    moment: the user is already leaving the view, so no draft is lost that the
 *    navigation wasn't discarding anyway. We don't reload a backgrounded tab —
 *    a user who tabs away and back expects their draft intact.
 *
 * 4. An unrecoverable SW cache state reloads immediately to recover, in any mode.
 *
 * Reloading/navigating picks up the new version only because `activateUpdate()`
 * promotes this client off its pinned version first (ngsw pins each client to
 * its version to avoid lazy-chunk 404s).
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  /** How often to poll the server for a newer version. */
  private static readonly CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

  private pollTimer: ReturnType<typeof setInterval> | undefined;

  /** True while running as an installed PWA — gates the silent auto-reload. */
  private autoReloadEnabled = false;

  /**
   * True once a new version has downloaded and is ready to activate. Lets us
   * defer promotion to a safe moment — backgrounding (installed PWA) or the
   * next navigation (browser tab) — instead of reloading out from under the user.
   */
  private updatePending = false;

  /**
   * Wire up update detection. Called once at app startup (app.config.ts
   * app initializer). No-op when the service worker is not enabled (dev builds,
   * unsupported browsers), so it is safe to call unconditionally.
   */
  init(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.autoReloadEnabled = this.isInstalledPwa();

    // Recover from a broken cache state the SW cannot repair. This is an error
    // path (the app may already be unusable), so reload immediately in any mode.
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.reloadToLatest(false));

    // Mark a downloaded version ready in every mode; how we promote to it
    // differs (installed: on background; browser tab: on next navigation).
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updatePending = true;
        // Installed PWA already hidden (e.g. background poll fired): reload now.
        if (this.autoReloadEnabled && document.visibilityState === 'hidden') {
          void this.reloadToLatest(true);
        }
      });

    // Browser tabs promote on the next in-app navigation — the user is leaving
    // the current view, so a hard-navigate to the target loses no draft the
    // navigation wasn't already discarding, and lands them on the new version.
    if (!this.autoReloadEnabled) {
      this.router.events
        .pipe(
          filter(
            (evt): evt is NavigationStart => evt instanceof NavigationStart,
          ),
          filter(() => this.updatePending),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((evt) => void this.reloadToLatest(true, evt.url));
    }

    // Poll for updates and re-check on tab focus. These fire no UI (only a
    // background network check), so run them outside Angular to avoid a
    // change-detection cycle on every tab switch. Crucially, an in-zone periodic
    // timer would keep NgZone perpetually unstable and stall the SW's
    // `registerWhenStable` registration until its 30s timeout on every load.
    this.zone.runOutsideAngular(() => {
      void this.checkForUpdate();
      this.pollTimer = setInterval(
        () => void this.checkForUpdate(),
        PwaUpdateService.CHECK_INTERVAL_MS,
      );
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    });

    this.destroyRef.onDestroy(() => {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    });
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      // Re-check on focus so a long-lived tab notices deployments promptly.
      void this.checkForUpdate();
      return;
    }
    // Tab/app just backgrounded — the safe moment to swap an installed PWA to a
    // downloaded update without the user seeing the reload.
    if (this.autoReloadEnabled && this.updatePending) {
      void this.reloadToLatest(true);
    }
  };

  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (err) {
      // Network hiccup or SW not yet controlling the page — try again next tick.
      console.error('[PwaUpdate] checkForUpdate failed', err);
    }
  }

  /**
   * Activate the downloaded version (when `activateFirst`) and load into it.
   * With `targetUrl`, hard-navigates there (browser-tab navigation promotion);
   * otherwise reloads the current URL. Activation is skipped on the
   * unrecoverable path, where a plain reload lets ngsw re-bootstrap.
   */
  private async reloadToLatest(
    activateFirst: boolean,
    targetUrl?: string,
  ): Promise<void> {
    // A visibilitychange to `hidden` also fires while MSAL navigates to Entra.
    // Keep the downloaded update pending and activate it on a later background
    // transition, after MSAL has cleared its marker and consumed the response.
    if (
      activateFirst &&
      isMsalInteractionInProgress(window.location, window.sessionStorage)
    ) {
      console.info(
        '[PwaUpdate] Deferring update reload until the MSAL interaction completes.',
      );
      return;
    }

    try {
      if (activateFirst) {
        await this.swUpdate.activateUpdate();
      }
    } catch (err) {
      // Activation can fail if the SW is busy; reload anyway — a fresh
      // navigation still picks up the newest cached version.
      console.error('[PwaUpdate] activateUpdate failed', err);
    } finally {
      if (targetUrl) {
        document.location.assign(targetUrl);
      } else {
        document.location.reload();
      }
    }
  }

  /**
   * True when the app is running as an installed PWA rather than a browser tab.
   * Covers the standard installed display modes plus the iOS Safari legacy flag.
   */
  private isInstalledPwa(): boolean {
    const installedModes = [
      'standalone',
      'fullscreen',
      'minimal-ui',
      'window-controls-overlay',
    ];
    const byDisplayMode = installedModes.some(
      (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches,
    );
    const iosStandalone =
      (window.navigator as unknown as { standalone?: boolean }).standalone ===
      true;
    return byDisplayMode || iosStandalone;
  }
}
